import React, { useState } from 'react';
import { useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/lib/shadcn/components/ui/card';
import {
  PageHeader,
  SharedAlertDialog,
  useAlertDialog,
} from '@/shared/components';
import { CustomerForm, CustomerFormData } from '../components/CustomerForm';
import { useCustomerMutations } from '../queries/customers.query';
import { BackendErrorResponse } from '@/shared/models/common.model';
import { useUnsavedChangesBlocker } from '@/shared';

export const CustomerCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const confirmDialog = useAlertDialog();
  const { createCustomer } = useCustomerMutations();
  const { t } = useTranslation('customers');

  const [isDirty, setIsDirty] = useState(false);

  const { handleConfirmLeave, handleCancelLeave } = useUnsavedChangesBlocker({
    isDirty,
    confirmDialog,
  });
  const { isOpen: isAlertOpen, hideAlert } = confirmDialog;

  const handleGoBack = () => {
    if (canGoBack) {
      router.history.back();
    } else {
      router.navigate({ to: '/customers' });
    }
  };

  const handleSubmit = async (data: CustomerFormData) => {
    handleConfirmLeave();
    await createCustomer.mutateAsync(data);
    navigate({ to: '/customers' });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <PageHeader
        title={t('form.title')}
        description={t('form.subTitle')}
        action={{
          label: t('backToCustomers'),
          onClick: handleGoBack,
          icon: <ArrowLeft className="h-4 w-4 mr-2" />,
        }}
      />
      <Card>
        <CardContent>
          <CustomerForm
            mode="create"
            onSubmit={handleSubmit}
            isLoading={createCustomer.isPending}
            translate={t}
            error={
              (createCustomer.error as BackendErrorResponse)?.response?.data
                ?.message
            }
            onDirtyChange={setIsDirty}
          />
        </CardContent>
        <SharedAlertDialog
          open={isAlertOpen}
          onOpenChange={hideAlert}
          title={t('form.discardChange')}
          description={t('form.discardChangeDesc')}
          onConfirm={handleConfirmLeave}
          onCancel={handleCancelLeave}
          variant="destructive"
          confirmText={t('form.discard')}
        />
      </Card>
    </div>
  );
};
